import React, { useState, useRef } from 'react';
import { apiService } from '../services/api';
import logger from '../utils/logger';

interface ActionObjectPair {
  action: string;
  object: string;
  modifier?: string;
  structure?: string;
  measurement?: {
    value: number;
    unit: string;
  };
}

interface ClassificationResult {
  intents: ActionObjectPair[];
}

interface VoiceClassifierProps {
  sessionId: string;
  provider: 'openai' | 'ollama';
  ollamaModel: string;
  openaiModel: string;
}

const VoiceClassifier: React.FC<VoiceClassifierProps> = ({ sessionId, provider, ollamaModel, openaiModel }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [results, setResults] = useState<ClassificationResult | null>(null);
  const [transcription, setTranscription] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  const startRecording = async () => {
    try {
      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('getUserMedia is not supported in this browser or requires HTTPS');
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        await processAudio(audioBlob);
        
        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setError(null);
    } catch (err) {
      let errorMessage = 'Failed to access microphone.';
      
      if (err instanceof Error) {
        if (err.message.includes('getUserMedia is not supported')) {
          errorMessage = 'Voice recording requires HTTPS or localhost. Please use a secure connection.';
        } else if (err.name === 'NotAllowedError') {
          errorMessage = 'Microphone access denied. Please allow microphone permissions and try again.';
        } else if (err.name === 'NotFoundError') {
          errorMessage = 'No microphone found. Please connect a microphone and try again.';
        } else if (err.name === 'NotSupportedError') {
          errorMessage = 'Your browser does not support audio recording.';
        }
      }
      
      setError(errorMessage);
      logger.error('Recording error:', err);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setLoading(true);
    }
  };

  const processAudio = async (audioBlob: Blob) => {
    try {
      // Always use OpenAI for voice classification, even if Ollama is selected
      const effectiveProvider = provider === 'ollama' ? 'openai' : provider;
      const response = await apiService.classifyVoice(audioBlob, sessionId, effectiveProvider, openaiModel);
      setResults(response);
      setTranscription(response.transcription || '');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to process audio. Please try again.';
      setError(errorMessage);
      logger.error('Processing error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setResults(null);
    setTranscription('');
    setError(null);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            Voice Intent Classification
          </h2>
          <div className="text-sm text-gray-600">
            Using: <span className="font-semibold text-blue-600">{provider === 'openai' ? 'OpenAI' : 'Ollama (Fallback to OpenAI)'}</span>
          </div>
        </div>

        {provider === 'ollama' && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  Voice classification is not supported with Ollama. Using OpenAI as fallback for this feature.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="text-center py-8">
          {!isRecording && !loading && (
            <button
              onClick={startRecording}
              className="inline-flex items-center px-6 py-3 bg-red-600 text-white font-medium rounded-full hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors duration-200"
            >
              <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
              </svg>
              Start Recording
            </button>
          )}

          {isRecording && (
            <div className="space-y-4">
              <div className="flex items-center justify-center">
                <div className="animate-pulse bg-red-500 rounded-full w-4 h-4 mr-3"></div>
                <span className="text-red-600 font-medium">Recording...</span>
              </div>
              <button
                onClick={stopRecording}
                className="inline-flex items-center px-6 py-3 bg-gray-600 text-white font-medium rounded-full hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd" />
                </svg>
                Stop Recording
              </button>
            </div>
          )}

          {loading && (
            <div className="space-y-4">
              <div className="inline-flex items-center px-6 py-3 bg-blue-100 text-blue-800 font-medium rounded-full">
                <svg className="animate-spin w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" strokeDasharray="80" strokeDashoffset="60"></circle>
                </svg>
                Processing Audio...
              </div>
            </div>
          )}
        </div>

        {(results || transcription) && (
          <div className="mt-6 flex justify-center">
            <button
              onClick={handleClear}
              className="px-4 py-2 bg-gray-600 text-white font-medium rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Clear Results
            </button>
          </div>
        )}

        {error && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  {error}
                </div>
              </div>
            </div>
          </div>
        )}

        {transcription && (
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Transcription:
            </h3>
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-blue-800 italic">"{transcription}"</p>
            </div>
          </div>
        )}

        {results && (
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Classification Results:
            </h3>
            
            {results.intents.length > 0 ? (
              <div className="space-y-4">
                {results.intents.map((pair, index) => (
                  <div key={index} className="p-4 bg-green-50 border border-green-200 rounded-md">
                    <h4 className="text-md font-semibold text-gray-800 mb-2">
                      Action-Object Pair {results.intents.length > 1 ? `#${index + 1}` : ''}:
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <span className="text-sm font-medium text-gray-600">Action:</span>
                        <div className="ml-2">
                          <span className="text-sm font-bold text-green-800">
                            {pair.action}
                          </span>
                        </div>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-600">Object:</span>
                        <div className="ml-2">
                          <span className="text-sm font-bold text-green-800">
                            {pair.object}
                          </span>
                        </div>
                      </div>
                      {pair.modifier && (
                        <div>
                          <span className="text-sm font-medium text-gray-600">Modifier:</span>
                          <div className="ml-2">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                              {pair.modifier}
                            </span>
                          </div>
                        </div>
                      )}
                      {pair.structure && (
                        <div>
                          <span className="text-sm font-medium text-gray-600">Structure:</span>
                          <div className="ml-2">
                            <span className="px-2 py-1 bg-amber-100 text-amber-800 text-sm rounded-full font-semibold">
                              {pair.structure}
                            </span>
                          </div>
                        </div>
                      )}
                      {pair.measurement && (
                        <div>
                          <span className="text-sm font-medium text-gray-600">Measurement:</span>
                          <div className="ml-2">
                            <span className="px-2 py-1 bg-purple-100 text-purple-800 text-sm rounded-full font-semibold">
                              {pair.measurement.value}{pair.measurement.unit ? ` ${pair.measurement.unit}` : ''}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
                <p className="text-sm text-gray-600">
                  No valid action-object pairs were identified in the transcribed text.
                </p>
              </div>
            )}
          </div>
        )}

        {results && results.intents.length === 0 && transcription && !loading && !error && (
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="text-sm text-yellow-700">
              No action-object pairs were identified in the transcribed audio.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceClassifier;
